import { useState, useEffect, useCallback } from 'react'
import { RotateCcw, ChevronDown, ChevronRight, Save } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import { useAuth } from '../App'
import { showSuccessToast, showErrorToast } from '@/components/NotificationToast'
import { systemPromptsApi, type SystemPrompt } from '@/services/promptService'

export function SystemPromptsPage() {
  const { isSuperAdmin } = useAuth()
  const [prompts, setPrompts] = useState<SystemPrompt[]>([])
  const [loading, setLoading] = useState(true)
  const [expandedKey, setExpandedKey] = useState<string | null>(null)
  const [editContent, setEditContent] = useState('')
  const [saving, setSaving] = useState(false)
  const [resetConfirmKey, setResetConfirmKey] = useState<string | null>(null)

  const fetchPrompts = useCallback(async () => {
    try {
      const data = await systemPromptsApi.list()
      setPrompts(data)
    } catch {
      showErrorToast('Failed to load system prompts')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchPrompts()
  }, [fetchPrompts])

  if (!isSuperAdmin()) {
    return (
      <div className="rounded-lg border border-neutral-200 bg-white p-8 text-center">
        <h2 className="text-2xl font-bold text-red-500 mb-4">Access Denied</h2>
        <p className="text-gray-500">Only super admins can access system prompts.</p>
      </div>
    )
  }

  const handleExpand = (key: string, prompt: SystemPrompt) => {
    if (expandedKey === key) {
      setExpandedKey(null)
    } else {
      setExpandedKey(key)
      setEditContent(prompt.content)
    }
  }

  const handleSave = async (key: string) => {
    setSaving(true)
    try {
      await systemPromptsApi.update(key, editContent)
      showSuccessToast('Prompt updated')
      await fetchPrompts()
    } catch {
      showErrorToast('Failed to update prompt')
    } finally {
      setSaving(false)
    }
  }

  const handleReset = async (key: string) => {
    try {
      await systemPromptsApi.reset(key)
      setResetConfirmKey(null)
      const updated = await systemPromptsApi.get(key)
      setEditContent(updated.content)
      showSuccessToast('Prompt reset to default')
      await fetchPrompts()
    } catch {
      showErrorToast('Failed to reset prompt')
    }
  }

  const getOriginalContent = (key: string) => {
    return prompts.find(p => p.prompt_key === key)?.content ?? ''
  }

  const hasChanges = (key: string) => {
    return editContent !== getOriginalContent(key)
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-foreground mb-2 page-main-heading">System Prompts</h2>
        <p className="text-sm text-muted-foreground">
          Manage AI system prompts used across the platform. Changes take effect immediately for all AI operations.
        </p>
      </div>

      {loading ? (
        <div className="text-center py-12 text-muted-foreground">Loading...</div>
      ) : (
        <div className="space-y-3">
          {prompts.map((prompt) => (
            <Card key={prompt.prompt_key} className="overflow-hidden">
              <button
                onClick={() => handleExpand(prompt.prompt_key, prompt)}
                className="w-full p-4 flex items-center gap-3 text-left hover:bg-muted/30 transition-colors"
              >
                {expandedKey === prompt.prompt_key ? (
                  <ChevronDown className="h-4 w-4 text-muted-foreground shrink-0" />
                ) : (
                  <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-medium text-sm">{prompt.display_name}</span>
                    {prompt.is_customized ? (
                      <Badge variant="outline" className="text-amber-600 border-amber-300 bg-amber-50">
                        Customized
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="text-muted-foreground">
                        Default
                      </Badge>
                    )}
                  </div>
                  {prompt.description && (
                    <p className="text-xs text-muted-foreground truncate">{prompt.description}</p>
                  )}
                </div>
                <div className="flex gap-1 shrink-0">
                  {prompt.template_variables.map((v) => (
                    <Badge key={v} variant="secondary" className="text-xs font-mono">
                      ${v}
                    </Badge>
                  ))}
                </div>
              </button>

              {expandedKey === prompt.prompt_key && (
                <div className="border-t px-4 pb-4 pt-3 space-y-3">
                  <textarea
                    value={editContent}
                    onChange={(e) => setEditContent(e.target.value)}
                    className="w-full min-h-[300px] rounded-md border border-input bg-background px-3 py-2 text-xs font-mono ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 leading-relaxed"
                    spellCheck={false}
                  />
                  <div className="flex items-center justify-between">
                    <div>
                      {prompt.is_customized && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setResetConfirmKey(prompt.prompt_key)}
                        >
                          <RotateCcw className="h-3.5 w-3.5 mr-2" />
                          Reset to Default
                        </Button>
                      )}
                    </div>
                    <Button
                      size="sm"
                      onClick={() => handleSave(prompt.prompt_key)}
                      disabled={saving || !hasChanges(prompt.prompt_key)}
                    >
                      <Save className="h-3.5 w-3.5 mr-2" />
                      {saving ? 'Saving...' : 'Save Changes'}
                    </Button>
                  </div>
                </div>
              )}
            </Card>
          ))}
        </div>
      )}

      {/* Reset Confirmation Dialog */}
      <Dialog open={!!resetConfirmKey} onOpenChange={() => setResetConfirmKey(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reset to Default</DialogTitle>
            <DialogDescription>
              Are you sure you want to reset this prompt to its default content? Your customizations will be lost.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResetConfirmKey(null)}>Cancel</Button>
            <Button variant="destructive" onClick={() => resetConfirmKey && handleReset(resetConfirmKey)}>
              Reset
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
